import { useCallback, useRef, useState } from 'react';

import { useCreateUploadUrlMutation, useUploadFileToS3Mutation } from '@/store/api';
import { getMimeTypeByFile } from '@/utils';

export const useFileUpload = () => {
	const createUrlRef = useRef<any>(null);
	const fileUploadRef = useRef<any>(null);

	const [loading, setLoading] = useState(false);
	const [fileId, setFileId] = useState<string | null>(null);
	const [fileUrl, setFileUrl] = useState('');
	const [destination, setDestination] = useState<string | null>('');

	const [createUrl] = useCreateUploadUrlMutation();
	const [startFileUpload] = useUploadFileToS3Mutation();

	const startUpload = useCallback(
		async (file: any, destination: any, isImage: any) => {
			try {
				if (createUrlRef.current || fileUploadRef.current) {
					return;
				}

				setFileId(null);
				setLoading(true);
				setDestination(null);

				const createUrlAction = createUrl({
					file: {
						name: file.name,
						mimeType: getMimeTypeByFile(file),
						destination,
					},
					isImage,
				});
				createUrlRef.current = createUrlAction;

				const { url, fileId } = await createUrlAction.unwrap();

				const fileUploadAction = startFileUpload({ url, file });
				fileUploadRef.current = fileUploadAction;

				await fileUploadAction.unwrap();

				setFileId(fileId);
				setFileUrl(url);
				setDestination(destination);
				return { fileId, url };
			} catch (error) {
				console.error(error);
				throw new Error('Failed to upload image');
			} finally {
				createUrlRef.current = null;
				fileUploadRef.current = null;
				setLoading(false);
			}
		},
		[createUrl, startFileUpload]
	);

	const abortUpload = useCallback(() => {
		createUrlRef.current?.abort();
		fileUploadRef.current?.abort();
	}, []);

	return {
		startUpload,
		abortUpload,
		loading,
		fileId,
		fileUrl,
		destination,
	};
};
